import { Document, DocumentSummary } from "./Document.dto";
import { DocumentSignSummary } from "./DocumentSign.dto";
import { FolderFileResource, FolderFileType } from "./FolderFile.dto";
import { FolderSharingRole, FolderSharingRoleBy, FolderSharingSummary } from "./FolderSharing.dto";
import { Proposal, ProposalSummary } from "./Proposal.dto";
import { Sign, SignSignerAttr, SignSummary } from "./Sign.dto";
import { Paginatable } from "../common/Paginatable.dto";
import { UserEssential } from "../sso/User.dto";

export enum FolderType {
  NORMAL = "NORMAL",
  ARCHIVER = "ARCHIVER",
  ECONSENT = "ECONSENT",
}

export type FolderTypeWithNone = FolderType | "NONE";

export class FolderCriteria {
  static FOLDER_NAME_MAXLEN = 256;
}

export interface Folder {
  id: bigint;
  organizationId: bigint;
  parentFolderId: bigint;
  name: string;
  folderType: FolderType;
  isDestroyed: boolean;
}

export interface FolderSummary {
  id: bigint;
  name: string;
  folderType?: FolderType;
}

export interface FolderResource extends Folder {
  createdBy: UserEssential;
  createdAt: Date;
}

export interface PaginateFoldersRequest {
  organizationId: bigint;
  parentFolderId?: bigint;
  page: number;
  rpp: number;
}

export type PaginateFoldersResponse = Paginatable<FolderResource>;

export interface GetFoldersSummaryRequest {
  organizationId: bigint;
  folderIds: string;
  folderType?: FolderType;
  includeDeletedResource?: boolean;
}

export interface GetFoldersSummaryResponse {
  folders: FolderSummary[];
}

export interface GetFolderByIdRequest {
  folderId: bigint;
}

export interface GetFolderByIdResponse {
  folder: Folder;
}

export interface CreateFolderRequest {
  organizationId: bigint;
  parentFolderId?: bigint;
  name: string;
  folderType?: FolderType;
}

export interface CreateFolderResponse {
  folder: Folder;
}

export enum FolderTraverseType {
  Folder = "FOLDER",
  File = "FILE",
}

export type FolderTraverse = FolderTraverseEntry | FolderFileTraverseEntry;

export interface FolderTraverseEntry {
  traverseType: FolderTraverseType.Folder;
  folderType?: FolderType;
  parentFolderId: bigint;
  organizationId: bigint;
  id: bigint;
  name: string;
  sharings?: FolderSharingSummary[];
}

export interface FolderFileTraverseEntry {
  traverseType: FolderTraverseType.File;
  fileType: FolderFileType;
  organizationId: bigint;
  id: bigint;
  name: string;
  sharings?: FolderSharingSummary[];
}

export enum BrowseEntryType {
  Folder = "FOLDER",
  Document = "DOCUMENT",
  Proposal = "PROPOSAL",
  Sign = "SIGN",
  Unknown = "UNKNOWN",
}

export type BrowseEntrySummary =
  | FolderEntry
  | DocumentEntry<DocumentSummary>
  | ProposalEntry<ProposalSummary>
  | SignEntry<SignSummary, DocumentSummary>
  | UnknownFileEntry;

export type FolderFileEntry = DocumentEntry | ProposalEntry | SignEntry | UnknownFileEntry;
export type BrowseEntry = FolderEntry | FolderFileEntry;

type AndRole = { role: FolderSharingRole; roleBy: FolderSharingRoleBy };

export type DocumentEntryAndRole = DocumentEntry & AndRole;
export type ProposalEntryAndRole = ProposalEntry & AndRole;
export type SignEntryAndRole = SignEntry & AndRole;
export type UnknownFileEntryAndRole = UnknownFileEntry & AndRole;
export type FolderFileEntryAndRole = DocumentEntryAndRole | ProposalEntryAndRole | SignEntryAndRole | UnknownFileEntryAndRole;

export interface FolderEntry {
  type: BrowseEntryType.Folder;
  resource: FolderResource;
  isManager: boolean;
  role?: FolderSharingRole;
}

export interface UnknownFileEntry {
  type: BrowseEntryType.Unknown;
  resource: FolderFileResource;
  isManager: boolean;
  role?: FolderSharingRole;
}

export interface DocumentEntry<D = Document> {
  type: BrowseEntryType.Document;
  resource: FolderFileResource;
  document: D;
  isManager: boolean;
  role?: FolderSharingRole;
}

export interface ProposalEntry<P = Proposal> {
  type: BrowseEntryType.Proposal;
  resource: FolderFileResource;
  proposal: P;
  isManager: boolean;
  role?: FolderSharingRole;
}

export interface SignEntry<S = Sign, D = Document> {
  type: BrowseEntryType.Sign;
  resource: FolderFileResource;
  sign: S;
  document: D;
  documentSigns?: DocumentSignSummary[];
  signAttrs?: SignSignerAttr[];
  isManager: boolean;
  role?: FolderSharingRole;
}

export interface BrowseFolderRequest {
  organizationId: bigint;
  folderId?: bigint;
  folderType?: FolderType;
  type?: FolderFileType;
  includeDeletedResource?: boolean;
  includeFolderOnly?: boolean;
  includeFileOnly?: boolean;
  name?: string;
}

export interface BrowseFolderResponse {
  entries: BrowseEntrySummary[];
}

export interface RemoveFolderByIdRequest {
  folderId: bigint;
}

export interface RemoveFolderByIdResponse {
  removed: boolean;
}

export interface MoveFolderByIdRequest {
  folderId: bigint;
  destinationFolderId: bigint | null;
}

export interface MoveFolderByIdResponse {
  moved: boolean;
}

export interface RenameFolderByIdRequest {
  folderId: bigint;
  name: string;
}

export interface RenameFolderByIdResponse {
  renamed: boolean;
}

export interface GetMyRoleForFolderByIdRequest {
  folderId: bigint;
}

export interface GetMyRoleForFolderByIdResponse {
  role: FolderSharingRole;
}

export interface GetMyRoleForFolderFileByIdRequest {
  fileId: bigint;
}

export interface GetMyRoleForFolderFileByIdResponse {
  role: FolderSharingRole;
}
